// Finance module (contabilità): accounts, categories, transactions, and customizable charts.
import { z } from 'zod';

const Uuid = z.string().uuid();
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida');

export const ACCOUNT_KINDS = ['cash', 'bank', 'card', 'other'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
export const ACCOUNT_KIND_LABELS: Record<AccountKind, string> = { cash: 'Contanti', bank: 'Conto corrente', card: 'Carta', other: 'Altro' };

export const TRANSACTION_KINDS = ['income', 'expense'] as const;
export type TransactionKind = (typeof TRANSACTION_KINDS)[number];
export const TRANSACTION_KIND_LABELS: Record<TransactionKind, string> = { income: 'Entrata', expense: 'Uscita' };

export const DEFAULT_FINANCE_CATEGORIES: { name: string; kind: TransactionKind; color: string }[] = [
  { name: 'Vendite', kind: 'income', color: 'green' },
  { name: 'Servizi', kind: 'income', color: 'teal' },
  { name: 'Altre entrate', kind: 'income', color: 'lime' },
  { name: 'Fornitori', kind: 'expense', color: 'orange' },
  { name: 'Stipendi', kind: 'expense', color: 'red' },
  { name: 'Strumenti software', kind: 'expense', color: 'indigo' },
  { name: 'Marketing', kind: 'expense', color: 'pink' },
  { name: 'Altre uscite', kind: 'expense', color: 'purple' },
];

export const AccountInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(100),
  kind: z.enum(ACCOUNT_KINDS).default('bank'),
  openingBalanceCents: z.number().int().default(0),
});
export type AccountInput = z.input<typeof AccountInput>;

export const CategoryInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(80),
  kind: z.enum(TRANSACTION_KINDS),
  color: z.string().trim().max(20).nullable().default(null),
});
export type CategoryInput = z.input<typeof CategoryInput>;

const transactionFields = {
  accountId: Uuid,
  categoryId: Uuid.nullable(),
  kind: z.enum(TRANSACTION_KINDS),
  amountCents: z.number().int().min(1, 'Indica un importo'),
  occurredOn: DateOnly,
  description: z.string().trim().max(300).default(''),
};
export const CreateTransactionInput = z.object(transactionFields).partial({ categoryId: true, occurredOn: true, description: true });
export type CreateTransactionInput = z.input<typeof CreateTransactionInput>;
export const UpdateTransactionInput = z.object(transactionFields).partial();
export type UpdateTransactionInput = z.input<typeof UpdateTransactionInput>;

export const TransactionListQuery = z.object({
  accountId: Uuid.optional(),
  categoryId: Uuid.optional(),
  kind: z.enum(TRANSACTION_KINDS).optional(),
  from: DateOnly.optional(),
  to: DateOnly.optional(),
});
export type TransactionListQuery = z.input<typeof TransactionListQuery>;

// ------------------------------------------------------------------ customizable charts (shared with CRM, tasks)
export const WIDGET_MODULES = ['finance', 'crm', 'tasks'] as const;
export type WidgetModule = (typeof WIDGET_MODULES)[number];
export const CHART_TYPES = ['bar', 'line', 'donut'] as const;
export type ChartType = (typeof CHART_TYPES)[number];
export const DATE_RANGES = ['last_3_months', 'last_6_months', 'last_12_months', 'this_year', 'all_time'] as const;
export type DateRange = (typeof DATE_RANGES)[number];
export const DATE_RANGE_LABELS: Record<DateRange, string> = {
  last_3_months: 'Ultimi 3 mesi', last_6_months: 'Ultimi 6 mesi', last_12_months: 'Ultimi 12 mesi', this_year: 'Anno corrente', all_time: 'Da sempre',
};

export const FINANCE_METRICS = ['net', 'income', 'expense'] as const;
export const FINANCE_GROUP_BY = ['month', 'category', 'account'] as const;
export const CRM_METRICS = ['deal_value', 'deal_count'] as const;
export const CRM_GROUP_BY = ['month', 'stage', 'owner'] as const;
export const TASK_METRICS = ['task_count', 'completed_count'] as const;
export const TASK_GROUP_BY = ['week', 'assignee', 'priority', 'status'] as const;
export type Metric = (typeof FINANCE_METRICS)[number] | (typeof CRM_METRICS)[number] | (typeof TASK_METRICS)[number];
export type GroupBy = (typeof FINANCE_GROUP_BY)[number] | (typeof CRM_GROUP_BY)[number] | (typeof TASK_GROUP_BY)[number];

export const METRIC_LABELS: Record<Metric, string> = {
  net: 'Saldo netto', income: 'Entrate', expense: 'Uscite', deal_value: 'Valore trattative', deal_count: 'Numero trattative',
  task_count: 'Task create', completed_count: 'Task completate',
};
export const GROUP_BY_LABELS: Record<GroupBy, string> = {
  month: 'Mese', category: 'Categoria', account: 'Conto', stage: 'Fase', owner: 'Responsabile',
  week: 'Settimana', assignee: 'Assegnatario', priority: 'Priorità', status: 'Stato',
};

export const WidgetInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(60),
  chartType: z.enum(CHART_TYPES),
  /** Checkboxes: one or more metrics plotted together. A donut only ever plots the first one. */
  metrics: z.array(z.string().trim().min(1).max(40)).min(1, 'Scegli almeno una metrica').max(6),
  groupBy: z.string().trim().min(1).max(40),
  dateRange: z.enum(DATE_RANGES).default('last_6_months'),
});
export type WidgetInput = z.input<typeof WidgetInput>;

export interface Widget {
  id: string;
  workspaceId: string;
  module: WidgetModule;
  /** Set when module is 'tasks': which custom section this chart belongs to. */
  sectionId: string | null;
  name: string;
  chartType: ChartType;
  metrics: string[];
  groupBy: string;
  dateRange: DateRange;
  position: number;
}

export interface ChartPoint {
  label: string;
  value: number;
}

export interface ChartLine {
  metric: string;
  points: ChartPoint[];
  total: number;
}

export interface ChartSeries {
  widget: Widget;
  lines: ChartLine[];
}

// ------------------------------------------------------------------ responses
export interface FinanceAccount {
  id: string;
  workspaceId: string;
  name: string;
  kind: AccountKind;
  openingBalanceCents: number;
  balanceCents: number;
}

export interface FinanceCategory {
  id: string;
  workspaceId: string;
  name: string;
  kind: TransactionKind;
  color: string | null;
}

export interface FinanceTransaction {
  id: string;
  workspaceId: string;
  kind: TransactionKind;
  amountCents: number;
  occurredOn: string;
  description: string;
  account: { id: string; name: string };
  category: { id: string; name: string; color: string | null } | null;
  createdBy: { id: string; displayName: string };
  createdAt: string;
  canEdit: boolean;
}

export interface FinanceConfig {
  workspaceId: string;
  enabled: boolean;
  currency: string;
  accounts: FinanceAccount[];
  categories: FinanceCategory[];
  permissions: { view: boolean; create: boolean; edit: boolean; delete: boolean; admin: boolean };
}
